/* sis-option-tiles behaviors.
   1. First visit: the default size from
      product.metafields.custom.default_size_option. Return visit: the
      customer's last size / frame / background for that product, remembered
      in localStorage (owner 2026-10-16). Only on a plain product URL (no
      ?variant=); the stock variant-picker.js handles everything after the
      click. Values are matched by input.value comparison, never CSS
      selectors - size values contain quote characters (12" X 16").
   2. Size -> subjects capacity (2026-08-05 redesign, sis-studio contract):
      capacity flows ONE WAY. Tiles are never disabled and no limit note
      renders; every size stays pickable at any subject count. The SELECTED
      size's data-capacity ("min-max", config subject_limits) is announced as
      document CustomEvent 'sis:size-capacity' {productId, max, label,
      globalMax} (announceCapacity: on init, on change, after morph churn);
      the app block's stepper ranges to globalMax (the largest size's cap)
      and hints from max/label. The app block dispatches
      'sis:subjects-changed' {count} back; a count past the selected size's
      cap auto-upsizes - autoUpsize clicks the smallest size that fits, and
      the morph + announceCapacity follow. Picking a smaller size clamps the
      stepper down app-side. The picker morphs its subtree on every variant
      change, wiping JS state - a MutationObserver re-applies. Delta tags and
      image swatches are Liquid-rendered by the same morph, with the instant
      JS recompute below (3./4.). */
(function () {
  // the editor re-executes scripts on section reload; the document-level
  // listeners below must bind once
  if (window.__sisOptionTiles) return;
  window.__sisOptionTiles = true;
  var appliedDefault = {};
  var PREFERS_REDUCED_MOTION =
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches;

  function maxOf(input) {
    var parts = (input.dataset.capacity || '').split('-');
    var max = parseInt(parts[parts.length - 1], 10);
    return isNaN(max) ? Infinity : max;
  }

  /* Load-time clicks must wait for our late-loading listeners. A tile click
     fires productSelect + 'sis:variant-preview'; sis-buy-summary and
     sis-gallery-badges (module scripts, defined after this one) render from
     those events, and one that is not defined yet misses the click and keeps
     the server's first-variant HTML for good. Both load-time clicks go through
     this gate: the default size below, and autoUpsize when a returning
     visitor's restored subject count is too big for the first variant
     (16 Oct 2026, live: 4 saved subjects showed '8" X 10" ... $179' in the
     summary while the cart had 12" X 16"). 2 s cap so a failed script never
     blocks the selection. After it resolves, clicks run at once as before. */
  var sisReady = null;
  var sisReadyDone = false;
  function whenSisListenersReady() {
    if (!sisReady) {
      var sisTags = ['sis-buy-summary', 'sis-gallery-badges'].filter(function (t) {
        return document.querySelector(t);
      });
      sisReady = Promise.race([
        Promise.all(sisTags.map(function (t) { return customElements.whenDefined(t); })),
        new Promise(function (resolve) { setTimeout(resolve, 2000); }),
      ]).then(function () { sisReadyDone = true; });
    }
    return sisReady;
  }

  /* First visit vs return (owner 2026-10-16: "default size should only run
     once on a fresh browser"). Every size / frame / background pick is
     remembered per product (localStorage sis-tiles:<productId>, no expiry).
     On a plain product URL (no ?variant=) the page restores that pick; only
     a browser with nothing remembered gets the default size
     (product.metafields.custom.default_size_option). Either way the size is
     then raised to the smallest one that holds a restored subject count, so
     the app block never clamps a saved count down to the size's cap. The
     selection is ONE click, made through the listener gate above: the other
     groups are set first without events, and the stock picker builds its
     request from every checked input. A human pick inside the wait wins. */
  var PICK_KEY = 'sis-tiles:';

  function rememberPick(picker) {
    var pid = picker.dataset.productId;
    if (!pid) return;
    var opts = {};
    picker.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
      if (input.dataset.optionName) opts[input.dataset.optionName] = input.value;
    });
    try { localStorage.setItem(PICK_KEY + pid, JSON.stringify({ t: Date.now(), opts: opts })); } catch (e) {}
  }

  function rememberedPick(pid) {
    try {
      var saved = JSON.parse(localStorage.getItem(PICK_KEY + pid) || 'null');
      return saved && saved.opts && typeof saved.opts === 'object' ? saved.opts : null;
    } catch (e) { return null; }
  }

  function radioFor(picker, name, value) {
    var found = null;
    picker.querySelectorAll('input[type="radio"]').forEach(function (input) {
      if (!found && input.dataset.optionName === name && input.value === value) found = input;
    });
    return found;
  }

  function checkedKey(picker) {
    return Array.prototype.map.call(picker.querySelectorAll('input[type="radio"]:checked'), function (input) {
      return input.value;
    }).join('|');
  }

  function applyInitialSelection(picker) {
    var pid = picker.dataset.productId;
    if (!pid || appliedDefault[pid]) return Promise.resolve();
    appliedDefault[pid] = true;
    if (new URLSearchParams(location.search).has('variant')) return Promise.resolve();
    var before = checkedKey(picker);
    return whenSisListenersReady().then(function () {
      if (checkedKey(picker) !== before) return; // the customer picked during the wait
      var plan = {};
      var remembered = rememberedPick(pid);
      if (remembered) {
        Object.keys(remembered).forEach(function (name) {
          if (radioFor(picker, name, remembered[name])) plan[name] = remembered[name];
        });
      }
      var sizeInput = picker.querySelector('input[type="radio"][data-capacity]');
      var sizeName = sizeInput && sizeInput.dataset.optionName;
      var wanted = picker.dataset.sisDefaultSize;
      // nothing remembered for the size (or that size is gone): the default
      if (sizeName && !plan[sizeName] && wanted) {
        var def = radioFor(picker, sizeName, wanted);
        if (def && def.dataset.optionAvailable !== 'false') plan[sizeName] = wanted;
      }
      // a restored subject count the planned size cannot hold: the smallest
      // size that does
      var count = picker.__sisSubjectCount;
      if (sizeName && typeof count === 'number') {
        var planned = plan[sizeName] ? radioFor(picker, sizeName, plan[sizeName])
          : picker.querySelector('input[type="radio"][data-capacity]:checked');
        if (planned && maxOf(planned) < count) {
          var fit = null;
          picker.querySelectorAll('input[type="radio"][data-capacity]').forEach(function (input) {
            if (!fit && !input.disabled && maxOf(input) >= count) fit = input;
          });
          if (fit) plan[sizeName] = fit.value;
        }
      }
      var changes = [];
      Object.keys(plan).forEach(function (name) {
        var radio = radioFor(picker, name, plan[name]);
        if (radio && !radio.checked) changes.push(radio);
      });
      if (!changes.length) return;
      var last = changes.pop();
      changes.forEach(function (radio) { radio.checked = true; });
      // the clicked radio's data-variant-id was rendered for the OLD
      // combination, so the stock picker may put the wrong ?variant= in the
      // address bar (a reload or a shared link would then open it). Once the
      // section answers, write the variant it actually resolved.
      if (changes.length) {
        picker.addEventListener('shopify:product:select', function (event) {
          if (!event.promise) return;
          event.promise.then(function (result) {
            var id = result && result.detail && result.detail.resource && result.detail.resource.id;
            if (!id) return;
            var url = new URL(window.location.href);
            if (!url.searchParams.has('variant') || url.searchParams.get('variant') === String(id)) return;
            url.searchParams.set('variant', String(id));
            history.replaceState({}, '', url.toString());
          }).catch(function () {});
        }, { once: true });
      }
      last.click();
    });
  }

  /* Capacity mode "disable" (block setting, owner 2026-09-03 - kept switchable
     so the two models can be A/B tested): sizes whose capacity is below the
     current subject count are disabled + greyed; the selected size auto-bumps
     up through autoUpsize. The default "clamp" mode never disables a tile
     (the stepper clamps down instead), so this is a no-op there. The count is
     remembered per picker because the morph re-renders the inputs. */
  function applyLimits(picker) {
    if (picker.dataset.sisCapacityMode !== 'disable') return;
    var count = picker.__sisSubjectCount;
    if (typeof count !== 'number') return;
    picker.querySelectorAll('input[type="radio"][data-capacity]').forEach(function (input) {
      var ok = count <= maxOf(input);
      input.disabled = !ok;
      var tile = input.closest('.sis-option-tile');
      if (tile) tile.classList.toggle('sis-option-tile--limited', !ok);
    });
  }

  /* 3. Instant price-delta tags: the Liquid-rendered tags only refresh when
     the section morph returns from the server (~1s perceived lag after a
     click). A variant price table is embedded per picker, so on any selection
     change every tile's tag is recomputed synchronously; the later morph
     reconciles to the same numbers. Upcharges only (+); an allowed-options
     list from the config scopes which options get tags. */
  function formatMoney(cents, currency) {
    // same output as Liquid's money_without_trailing_zeros (assets/sis-money.js)
    if (window.SisMoney) return window.SisMoney.text(cents / 100);
    var whole = cents % 100 === 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  }

  function recomputeDeltas(picker) {
    if (!('sisDeltaOptions' in picker.dataset)) return;
    var variants = variantTable(picker);
    if (!variants) return;
    var allowed = (picker.dataset.sisDeltaOptions || '').split('|').filter(Boolean);
    var currency = picker.dataset.sisCurrency || 'USD';

    var selection = readSelection(picker);
    function priceOf(opts) {
      var v = matchVariant(variants, opts);
      return v ? v.price : null;
    }
    var current = priceOf(selection);
    if (current == null) return;

    // pass 1: candidate price per tile
    var tiles = [];
    picker.querySelectorAll('input[type="radio"]').forEach(function (input) {
      var pos = parseInt((input.dataset.inputId || '').split('-')[0], 10);
      var body = input.nextElementSibling;
      if (!pos || !body) return;
      var candidate = selection.slice();
      candidate[pos - 1] = input.value;
      var price = priceOf(candidate);
      if (price == null) return;
      tiles.push({ input: input, body: body, price: price, option: input.dataset.optionName });
    });

    // pass 2: a real tag, or an EMPTY hidden one (owner 2026-10-14). The span
    // stays in place and its width comes from the CSS reservation (--sis-tag-min,
    // set per option in Liquid), so the tile never resizes. It must hold no text
    // while hidden: opacity 0 still exposes text to screen readers, crawlers and
    // agents, which read the old reserve ("+$150") as the selected size's price.
    tiles.forEach(function (t) {
      var span = t.body.querySelector('.sis-option-tile__price');
      if (!span) return;
      var allowedHere = allowed.length === 0 || allowed.indexOf(t.option) !== -1;
      var delta = t.price - current;
      // show/hide fade is the CSS opacity transition on the ghost class; a value
      // change on an already-visible tag gets a quick rise-and-fade pop
      var wasGhost = span.classList.contains('sis-option-tile__price--ghost');
      if (allowedHere && !t.input.checked && delta > 0) {
        var next = '+' + formatMoney(delta, currency);
        var changedInPlace = !wasGhost && span.textContent !== next;
        span.classList.remove('sis-option-tile__price--ghost');
        span.removeAttribute('aria-hidden');
        span.textContent = next;
        if (changedInPlace && !PREFERS_REDUCED_MOTION) {
          // fade only, no movement (owner). Inline opacity 0 + reflow + clear
          // lets the existing CSS opacity transition fade the new value in.
          // No WAAPI here: an opacity animation would outrank the CSS class
          // that owns ghost visibility (learned the hard way).
          span.style.opacity = '0';
          void span.offsetWidth;
          span.style.opacity = '';
        }
      } else {
        span.classList.add('sis-option-tile__price--ghost');
        span.setAttribute('aria-hidden', 'true');
        span.textContent = '';
      }
    });
  }

  /* 4. Instant swatch images: the driver-aware photo is resolved in Liquid, so
     without this it only swaps when the morph returns (~1s after a Framing
     click). Same map, applied in the browser the moment either option changes;
     the morph then re-renders identical src values. */
  function recomputeSwatches(picker) {
    var swatchOption = picker.dataset.sisSwatchOption;
    var driverOption = picker.dataset.sisDriverOption;
    if (!swatchOption || !driverOption) return;
    var mapEl = picker.querySelector('[data-sis-swatch-map]');
    if (!mapEl) return;
    var map;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }

    var driverValue = null;
    picker.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
      if (input.dataset.optionName === driverOption) driverValue = input.value;
    });
    if (!driverValue) return;

    picker.querySelectorAll('input[type="radio"]').forEach(function (input) {
      if (input.dataset.optionName !== swatchOption) return;
      var url = (map[input.value] || {})[driverValue];
      if (!url) return;
      // same CDN resize as the Liquid img src (480 keeps ~150-220px tiles at
      // 2x now that the DS photo fills the tile; the old 132px cap is gone)
      // - identical strings keep the src compare a no-op after each morph, so
      // this value MUST match blocks/sis-option-tiles.liquid's img width param
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'width=480';
      var img = input.nextElementSibling?.querySelector('.sis-option-tile__swatch--image img');
      if (img && img.getAttribute('src') !== url) crossfadeSwatch(img, url);
    });
  }

  /* URLs the browser has finished FETCHING - fed by preloadSwatches and by
     every completed swap. Skips the network preload; it deliberately does NOT
     skip the decode() gate below, because "fetched" and "decoded" are different
     things on iOS. */
  var decodedSwatches = {};

  /* Flicker-free swatch swap (owner 2026-08-22, iPhone: "almost no animation +
     flickering"). iOS Safari decodes images lazily at paint time and evicts
     decoded bitmaps aggressively, so ANY src assignment to a visible <img> can
     paint blank for a few frames while the new file rasterizes - the previous
     design committed the new src underneath a fading overlay, and that blank
     showing through the half-faded overlay was the flicker; the flash also ate
     the fade perceptually. (The original pre-rewrite comment warned about
     exactly this. It was right.)

     The rule now: NOTHING visible changes until the incoming bitmap is
     provably decoded.
     1. the real img keeps its old, already-painted src untouched;
     2. the NEW image goes in an overlay on top at opacity 0, and we await
        overlay.decode() - the API that resolves only when the bitmap is ready
        to paint (Safari 11.1+; a missing or rejected decode() just skips the
        gate - the fade still runs; only a failed network prefetch falls back
        to a plain swap via pre.onerror);
     3. only then the overlay fades IN over 200ms - on top of any previous
        click's overlay, which stays underneath to keep the picture continuous;
     4. on finish, the real img adopts the new src and we await ITS decode
        before removing the overlay, so the handoff frame is also gapless.
     Rapid clicks: the gen token still decides the winner; superseded overlays
     self-remove at their own commits and the winning commit sweeps stragglers,
     so bursts read as one continuous preview (rare edge: an uncached successor
     can briefly reveal the base img when a predecessor's own commit fires
     first - self-corrects when the successor decodes). A 600ms safety commit covers hidden tabs, where
     animations never advance. Reduced motion keeps the plain swap. */
  function crossfadeSwatch(img, url) {
    var gen = String(parseInt(img.dataset.sisSwapGen || '0', 10) + 1);
    img.dataset.sisSwapGen = gen;
    var isCurrent = function () {
      return img.isConnected && img.dataset.sisSwapGen === gen;
    };
    var wrap = img.parentElement;
    if (PREFERS_REDUCED_MOTION || typeof img.animate !== 'function' || !wrap) {
      img.setAttribute('src', url);
      return;
    }
    var begin = function () {
      if (!isCurrent()) return;
      var overlay = img.cloneNode(false);
      overlay.removeAttribute('data-sis-swap-gen');
      overlay.setAttribute('src', url);
      overlay.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('data-sis-overlay', gen);
      overlay.style.cssText =
        'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0;pointer-events:none;';
      wrap.appendChild(overlay);
      var ready = typeof overlay.decode === 'function'
        ? overlay.decode().catch(function () {})
        : Promise.resolve();
      ready.then(function () {
        if (!isCurrent()) { overlay.remove(); return; }
        // predecessors are NOT removed here: they sit underneath and keep the
        // picture continuous while this one fades in over them. Each superseded
        // overlay self-removes in its own commit (!isCurrent branch), and the
        // winning commit sweeps any stragglers.
        var anim = overlay.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 200,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'forwards',
        });
        var committed = false;
        var commit = function () {
          if (committed) return;
          committed = true;
          if (!isCurrent()) { overlay.remove(); return; }
          img.setAttribute('src', url);
          decodedSwatches[url] = true;
          var settled = typeof img.decode === 'function'
            ? img.decode().catch(function () {})
            : Promise.resolve();
          settled.then(function () {
            // sweep every overlay, not just our own - a burst can leave
            // superseded ones underneath whose own commits already ran
            wrap.querySelectorAll('[data-sis-overlay]').forEach(function (o) { o.remove(); });
          });
        };
        anim.onfinish = commit;
        anim.oncancel = commit;
        setTimeout(commit, 600);
      });
    };
    if (decodedSwatches[url]) {
      begin();
      return;
    }
    var pre = new Image();
    pre.onload = function () {
      decodedSwatches[url] = true;
      begin();
    };
    pre.onerror = function () {
      if (isCurrent()) img.setAttribute('src', url);
    };
    pre.src = url;
  }

  /* 5. Size -> subjects capacity (PDP redesign 2026-07-19): the app block's
     stepper clamps its max to the SELECTED size and rewrites its hint from
     this event. Capacity rides the size inputs as data-capacity (config
     subject_limits); the selected one is announced on init, on every change,
     and after morph churn. Contract: sis:size-capacity {productId, max, label,
     globalMax} (docs/config-data-model.md in sis-studio). */
  function announceCapacity(picker) {
    var checked = picker.querySelector('input[type="radio"][data-capacity]:checked');
    if (!checked) return;
    var max = maxOf(checked);
    if (!isFinite(max)) return;
    // global cap = the largest size's capacity (owner 2026-08-09: the stepper
    // ranges the whole catalog; exceeding the current size auto-upsizes)
    var globalMax = 0;
    picker.querySelectorAll('input[type="radio"][data-capacity]').forEach(function (input) {
      var m = maxOf(input);
      if (isFinite(m) && m > globalMax) globalMax = m;
    });
    try {
      document.dispatchEvent(new CustomEvent('sis:size-capacity', {
        detail: { productId: picker.dataset.productId, max: max, label: checked.value, globalMax: globalMax || max },
      }));
    } catch (e) { /* clamp is progressive enhancement */ }
  }

  /* subjects can drive size UP (owner 2026-08-09): when the count exceeds the
     selected size's capacity, select the smallest size that fits. Downward
     stays as-is: picking a smaller size clamps the stepper (DS clamp-down). */
  function autoUpsize(picker, count) {
    if (!sisReadyDone) {
      // during load: wait for the listeners, then judge the LATEST count
      // against the size selected then (the picker element survives its
      // morphs; its inputs do not, so nothing is held across the wait)
      if (picker.__sisUpsizePending) return;
      picker.__sisUpsizePending = true;
      // after the initial selection, which already sizes for the count
      whenSisListenersReady().then(function () { return picker.__sisInitial; }).then(function () {
        picker.__sisUpsizePending = false;
        var latest = picker.__sisSubjectCount;
        autoUpsize(picker, typeof latest === 'number' ? latest : count);
      });
      return;
    }
    var checked = picker.querySelector('input[type="radio"][data-capacity]:checked');
    if (!checked || maxOf(checked) >= count) return;
    var target = null;
    picker.querySelectorAll('input[type="radio"][data-capacity]').forEach(function (input) {
      if (target || input.disabled) return;
      if (maxOf(input) >= count) target = input;
    });
    if (target && !target.checked) target.click(); // morph + announceCapacity follow
  }

  /* 6. Seamless swatch swaps (owner 2026-08-09): the driver-aware images were
     fetched on FIRST use, so the first Framing flip waited on the network and
     the crossfade lagged. Warm every (value, driver) combination at idle time
     - the URLs are built EXACTLY like recomputeSwatches builds them, so the
     browser cache serves the swap instantly. A morph wipes the dataset flag;
     re-running is a cached no-op. */
  function preloadSwatches(picker) {
    if (picker.dataset.sisSwatchesPreloaded) return;
    var mapEl = picker.querySelector('[data-sis-swatch-map]');
    if (!mapEl) return;
    picker.dataset.sisSwatchesPreloaded = '1';
    var map;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }
    var urls = [];
    Object.keys(map).forEach(function (value) {
      var drivers = map[value] || {};
      Object.keys(drivers).forEach(function (d) {
        var url = drivers[d];
        if (url) urls.push(url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=480');
      });
    });
    if (!urls.length) return;
    var kick = function () {
      urls.forEach(function (u) {
        var img = new Image();
        img.onload = function () { decodedSwatches[u] = true; };
        img.src = u;
      });
    };
    // idle so it never competes with the gallery/LCP
    if ('requestIdleCallback' in window) requestIdleCallback(kick, { timeout: 3000 });
    else setTimeout(kick, 800);
  }


  /* current selection as a position-ordered array ("data-input-id" = "pos-idx"),
     and the variant row that matches it - shared by the delta tags and the
     gallery sync below. */
  function variantTable(picker) {
    var tableEl = picker.querySelector('[data-sis-variant-prices]');
    if (!tableEl) return null;
    var text = tableEl.textContent;
    if (picker.__sisTableText !== text) {
      try {
        picker.__sisTable = JSON.parse(text);
      } catch (e) {
        picker.__sisTable = null;
      }
      picker.__sisTableText = text;
    }
    return picker.__sisTable;
  }

  /* 8. One announcement per selection (code review 2026-09-24): the price row
     and the order summary used to re-read the selection and re-parse this
     table themselves. The picker now announces the matched variant once, as
     the bubbling event 'sis:variant-preview' {productId, price, compare, opts}
     (price/compare in cents, compare 0 when there is none); the blocks repaint
     from it at once and the server swap still has the last word. */
  function announceVariant(picker) {
    var variants = variantTable(picker);
    if (!variants) return;
    var v = matchVariant(variants, readSelection(picker));
    if (!v) return;
    picker.dispatchEvent(
      new CustomEvent('sis:variant-preview', {
        bubbles: true,
        detail: { productId: picker.dataset.productId, price: v.price, compare: v.compare || 0, opts: v.opts },
      })
    );
  }

  function readSelection(picker) {
    var selection = [];
    picker.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
      var pos = parseInt((input.dataset.inputId || '').split('-')[0], 10);
      if (pos) selection[pos - 1] = input.value;
    });
    return selection;
  }
  function matchVariant(variants, selection) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var hit = true;
      for (var j = 0; j < selection.length; j++) {
        if (selection[j] != null && v.opts[j] !== selection[j]) { hit = false; break; }
      }
      if (hit) return v;
    }
    return null;
  }

  /* 7. Instant MAIN-GALLERY swap (owner 2026-08-22: fast Framing clicks felt
     laggy). Horizon's pipeline is click -> section fetch -> media-gallery
     replaceWith(server HTML), so the big image could not move until a network
     round-trip after the LAST click - the fetch layer already aborts stale
     requests (variant-picker.js #abortController), so no click queue is needed
     here; the missing piece was a zero-latency first paint. Every variant's
     featured image is already a slide in the DOM (hide_variants is off), so on
     selection we snap the EXISTING slideshow to that slide immediately.
     animate:false on purpose: instant tracks fast clicks (last call wins),
     while a smooth scroll would queue easing and recreate the lag. The morph
     then lands a gallery whose FIRST slide is this same image, so the
     reconciliation is invisible. Variant without a featured image (media: 0)
     -> no-op, matching the server. */
  function syncGallery(picker) {
    var variants = variantTable(picker);
    if (!variants) return;
    var v = matchVariant(variants, readSelection(picker));
    if (!v || !v.media) return;
    var section = picker.closest('.shopify-section');
    var gallery = section && section.querySelector('media-gallery');
    if (!gallery) return;
    var slideshow = gallery.slideshow || gallery.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;
    var slides = gallery.querySelectorAll('slideshow-slide');
    for (var k = 0; k < slides.length; k++) {
      if (slides[k].querySelector('[data-media-id="' + v.media + '"]')) {
        if (slideshow.current !== k) slideshow.select(k, undefined, { animate: false });
        return;
      }
    }
  }

  /* 8. Skip the pointless gallery rebuild (owner 2026-08-22: Background Color
     clicks felt laggy). Horizon's media-gallery replaces its ENTIRE DOM with the
     fetched section's copy on every variant change - teardown, slideshow re-init,
     IntersectionObserver setup, relayout - measured at several 100ms+ main-thread
     tasks per click. On this catalog the gallery is VARIANT-INVARIANT (no variant
     has a featured image, so the fetched gallery is byte-identical to the one on
     screen) and all that work repaints the same pixels.

     Mechanism, all standards-level ordering, no upstream edits:
     - variant-picker dispatches "shopify:product:select" which BUBBLES to the
       document, and every consumer synchronously chains .then on event.promise
       during dispatch;
     - a document-level CAPTURE listener runs before the section-phase listeners,
       so its .then is FIRST in the promise's FIFO callback queue;
     - that callback removes <media-gallery> from the SHARED fetched document, and
       upstream's own handler then takes its `if (!newMediaGallery) return` exit -
       no replace, no rebuild. Price, buy buttons, picker morph, sku, inventory and
       sis-gallery-badges all read sibling nodes and are untouched (verified:
       badges re-renders from its OWN sis-gallery-badges node in the html).

     Why this can never break a morph (corrected after adversarial review - the
     first version of this comment claimed a combined-listing "main" morph was
     unreachable from tiles, which is FALSE: the tiles emit
     data-connected-product-url unconditionally, so if this product ever joins a
     combined listing, a tiles click does trigger updateMain): the protection is
     ORDERING, not reachability. Every variant-picker morph (updateMain,
     updateElement, its own picker morph) runs inside the fetch handler BEFORE
     the deferred event promise resolves, diffing against the still-intact html;
     the strip is a .then on that promise and only ever affects POST-resolution
     consumers - which is exactly one: media-gallery's replaceWith.

     Guards, in order:
     - only for selections made in OUR tiles picker (event target);
     - belt for future upstream reshuffles: a resolution that carries a new
       product is left untouched, so a cross-product gallery swap could never be
       suppressed even if the ordering above ever changed;
     - only when the picker's variant table exists and NO row carries a featured
       media id. The moment variant images are assigned in admin, this whole
       feature stands down and the stock pipeline (plus syncGallery's instant
       snap) takes over.

     Known, wanted behavior delta: a shopper browsing slide 3 is no longer
     yanked back to slide 1 by an option click - upstream's rebuild did that as
     a side effect. If someone reports "the gallery stopped resetting", it is
     this, and it is on purpose. */
  document.addEventListener(
    'shopify:product:select',
    function (e) {
      var target = e.target;
      if (!(target instanceof Element)) return;
      var picker = target.closest && target.closest('variant-picker.sis-option-tiles');
      if (!picker || !e.promise || typeof e.promise.then !== 'function') return;
      var variants = variantTable(picker);
      if (!variants) return;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].media) return; // variant images exist - never interfere
      }
      e.promise
        .then(function (r) {
          var detail = r && r.detail;
          if (!detail || detail.newProduct) return; // cross-product swap: hands off
          var html = detail.html;
          if (html && html.querySelector) {
            var g = html.querySelector('media-gallery');
            if (g) g.remove();
          }
        })
        .catch(function () { /* aborted fetch - nothing to strip */ });
    },
    true
  );

  var reapplyTimer = null;
  function initPicker(picker) {
    if (!picker.__sisInitial) picker.__sisInitial = applyInitialSelection(picker);
    applyLimits(picker);
    announceCapacity(picker);
    preloadSwatches(picker);
    if (picker.dataset.sisLimitsObserved) return;
    picker.dataset.sisLimitsObserved = '1';
    picker.addEventListener('change', function (e) {
      if (!e.target || !e.target.matches('input[type="radio"]')) return;
      rememberPick(picker);
      recomputeDeltas(picker);
      recomputeSwatches(picker);
      syncGallery(picker);
      announceCapacity(picker);
      announceVariant(picker);
    });
    // the morph re-renders the inputs on every variant change - re-announce
    // the selected size's capacity once the churn settles
    new MutationObserver(function () {
      clearTimeout(reapplyTimer);
      reapplyTimer = setTimeout(function () {
        applyLimits(picker);
        announceCapacity(picker);
      }, 120);
    }).observe(picker, { childList: true, subtree: true });
  }

  function initAll() {
    document.querySelectorAll('variant-picker.sis-option-tiles').forEach(initPicker);
  }

  document.addEventListener('sis:subjects-changed', function (e) {
    var d = (e && e.detail) || {};
    if (typeof d.count !== 'number') return;
    document.querySelectorAll('variant-picker.sis-option-tiles').forEach(function (picker) {
      picker.__sisSubjectCount = d.count;
      applyLimits(picker);
      autoUpsize(picker, d.count);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  // Theme Editor re-renders sections wholesale; pick up the rebuilt picker
  document.addEventListener('shopify:section:load', initAll);
})();
